import { useState, useEffect, useRef } from 'react';
import { Send, Loader2, WifiOff, Wifi } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { useWebSocketChat } from '@/hooks/useWebSocketChat';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/ui/page-header';
import { ResponsiveContainer } from './ResponsiveContainer';
import AdminJsonToggle from './AdminJsonToggle';
import type { Module } from '@/types';

interface ChatModuleData {
  welcomeMessage?: string;
  suggestedPrompts?: string[];
  minMessages?: number;
  allowSkip?: boolean;
}

interface DynamicChatModuleProps {
  module: Module;
  onComplete?: () => void;
}

export default function DynamicChatModule({ module, onComplete }: DynamicChatModuleProps) {
  const [inputMessage, setInputMessage] = useState('');
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  
  // Parse module data for UI configuration
  const chatConfig: ChatModuleData = module.data ? JSON.parse(module.data) : {};
  const { welcomeMessage, suggestedPrompts, minMessages = 0, allowSkip = false } = chatConfig;
  
  const { 
    messages, 
    sendMessage, 
    isConnected, 
    isLoading, 
    currentResponse,
    hasActiveResponse 
  } = useWebSocketChat();

  // Track progress based on message count
  const userMessageCount = messages.filter(m => m.fromUser).length;
  const canComplete = userMessageCount >= minMessages;

  const handleSendMessage = () => {
    if (inputMessage.trim() && isConnected && !hasActiveResponse) {
      sendMessage(inputMessage.trim());
      setInputMessage('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSuggestedPrompt = (prompt: string) => {
    if (isConnected && !hasActiveResponse) {
      sendMessage(prompt);
    }
  };

  // Auto-scroll to bottom when new messages arrive or during streaming
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages, currentResponse]);

  // Auto-focus input when response completes
  useEffect(() => {
    if (!hasActiveResponse && inputRef.current && isConnected) {
      inputRef.current.focus();
    }
  }, [hasActiveResponse, isConnected]);

  // Override welcome message if provided in config
  useEffect(() => {
    if (welcomeMessage && messages.length === 0 && !isLoading) {
      // This would typically be handled by the backend
      // For now, we'll just display it as part of the UI
    }
  }, [welcomeMessage, messages.length, isLoading]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white pb-20 safe-area-inset">
        <PageHeader 
          title={module.name || "AI Coaching"}
          variant="title-with-exit"
          onExit={() => navigate('/updates')}
        />
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-primary" role="status" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-20 safe-area-inset">
      <AdminJsonToggle 
        data={{
          module,
          chatConfig,
          messages,
          userMessageCount,
          canComplete,
          isConnected
        }}
        label="Chat Module Data"
      />
      <PageHeader 
        title={module.name || "AI Coaching"}
        variant="title-with-exit"
        onExit={() => navigate('/updates')}
      />
      
      <ResponsiveContainer maxWidth="7xl" className="py-6 h-[calc(100vh-200px)]">
        <div className="h-full flex flex-col bg-white rounded-lg shadow-sm border lg:max-w-4xl lg:mx-auto">
          {/* Connection Status Bar */}
          <div className={cn(
            "flex items-center justify-between px-4 py-2 border-b",
            isConnected ? "bg-green-50" : "bg-red-50"
          )}>
            <div className="flex items-center gap-2">
              {isConnected ? (
                <>
                  <Wifi className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-600">Connected</span>
                </>
              ) : (
                <>
                  <WifiOff className="h-4 w-4 text-red-600" />
                  <span className="text-sm text-red-600">Connecting...</span>
                </>
              )}
            </div>
            <span className="text-xs text-muted-foreground">
              AI Coach
            </span>
          </div>

          {/* Messages Area */}
          <ScrollArea ref={scrollAreaRef} className="flex-1 p-4">
            <div className="space-y-4">
              {/* Show custom welcome message if provided and no messages yet */}
              {welcomeMessage && messages.length === 0 && (
                <div className="flex justify-start">
                  <div className="max-w-[85%] sm:max-w-[80%] lg:max-w-[60%] rounded-lg px-4 py-3 bg-muted">
                    <p className="text-sm lg:text-base">{welcomeMessage}</p>
                    <p className="text-xs lg:text-sm text-muted-foreground mt-1">
                      {format(new Date(), 'h:mm a')}
                    </p>
                  </div>
                </div>
              )}

              {messages.map((message, index) => (
                <div
                  key={message.id || index}
                  className={cn(
                    "flex",
                    message.fromUser ? "justify-end" : "justify-start"
                  )}
                >
                  <div
                    className={cn(
                      "max-w-[85%] sm:max-w-[80%] lg:max-w-[60%] rounded-lg px-4 py-3 transition-all",
                      message.fromUser
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted hover:bg-muted/80"
                    )}
                  >
                    <p className="text-sm lg:text-base whitespace-pre-wrap">{message.text}</p>
                    <p className={cn(
                      "text-xs mt-1",
                      message.fromUser 
                        ? "text-primary-foreground/70" 
                        : "text-muted-foreground"
                    )}>
                      {format(new Date(message.timestamp), 'h:mm a')}
                    </p>
                  </div>
                </div>
              ))}

              {/* Current streaming response */}
              {currentResponse && (
                <div className="flex justify-start">
                  <div className="max-w-[85%] sm:max-w-[80%] lg:max-w-[60%] rounded-lg px-4 py-3 bg-muted">
                    <p className="text-sm lg:text-base whitespace-pre-wrap">{currentResponse}</p>
                    <div className="flex items-center gap-1 mt-1">
                      <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                      <p className="text-xs text-muted-foreground">AI is typing...</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Suggested Prompts */}
          {suggestedPrompts && suggestedPrompts.length > 0 && userMessageCount === 0 && (
            <div className="px-4 py-3 lg:px-6 lg:py-4 border-t border-b bg-gray-50">
              <p className="text-xs lg:text-sm text-muted-foreground mb-2">Suggested topics:</p>
              <div className="flex flex-wrap gap-2 lg:gap-3">
                {suggestedPrompts.map((prompt, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => handleSuggestedPrompt(prompt)}
                    disabled={!isConnected || hasActiveResponse}
                    className="text-xs lg:text-sm transition-all hover:scale-105 hover:bg-primary hover:text-primary-foreground"
                  >
                    {prompt}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className="p-4 lg:p-6 border-t bg-gray-50/50">
            <div className="flex gap-2 lg:gap-3">
              <Input
                ref={inputRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={isConnected ? "Type your message..." : "Connecting..."}
                disabled={!isConnected || hasActiveResponse}
                className="flex-1 h-10 lg:h-12 text-sm lg:text-base transition-all focus:ring-2 focus:ring-primary"
              />
              <Button
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || !isConnected || hasActiveResponse}
                size="icon"
                className="h-10 w-10 lg:h-12 lg:w-12 transition-all hover:scale-105"
              >
                {hasActiveResponse ? (
                  <Loader2 className="h-4 w-4 lg:h-5 lg:w-5 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 lg:h-5 lg:w-5" />
                )}
              </Button>
            </div>
          </div>

          {/* Progress and Completion */}
          {minMessages > 0 && (
            <div className="p-4 border-t">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-muted-foreground">
                  Messages sent: {userMessageCount} / {minMessages}
                </p>
                {canComplete && onComplete && (
                  <Button onClick={onComplete} variant="default" size="sm">
                    Continue
                  </Button>
                )}
                {allowSkip && !canComplete && onComplete && (
                  <Button onClick={onComplete} variant="outline" size="sm">
                    Skip for now
                  </Button>
                )}
              </div>
              {!canComplete && (
                <p className="text-xs text-muted-foreground">
                  Send {minMessages - userMessageCount} more message{minMessages - userMessageCount !== 1 ? 's' : ''} to continue
                </p>
              )}
            </div>
          )}
        </div>
      </ResponsiveContainer>
    </div>
  );
}